/**
 * Admin Dashboard for Eqaya Platform
 * Comprehensive platform administration interface
 */

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Tabs,
  Tab,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  LinearProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Badge,
  Tooltip,
  Switch,
  FormControlLabel,
  TablePagination,
  InputAdornment,
  Snackbar,
  CircularProgress,
  CardActions,
  ButtonGroup,
  Checkbox
} from '@mui/material';
import {
  Dashboard,
  People,
  Business,
  Payment,
  Security,
  Analytics,
  Settings,
  Warning,
  CheckCircle,
  Block,
  Visibility,
  Edit,
  Delete,
  Add,
  Download,
  Upload,
  Refresh,
  NotificationsActive,
  TrendingUp,
  TrendingDown,
  AttachMoney,
  Assessment,
  AdminPanelSettings,
  VerifiedUser,
  Report,
  Group,
  AccountBalance,
  Timeline,
  Speed,
  Search,
  FilterList,
  ErrorOutline,
  Schedule,
  MonetizationOn,
  Email,
  GetApp,
  Assignment,
  Verified,
  CloudUpload
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';
import RegionalDistributionPanel from '../components/admin/RegionalDistributionPanel';
import { useAuth } from '../contexts/AuthContext';
import DialogCloseButton from '../components/common/DialogCloseButton';
import AdminWithdrawals from '../components/admin/AdminWithdrawals';
import AdminRefunds from '../components/admin/AdminRefunds';
import adminService from '../services/adminService';
import { formatCurrency, formatDate, formatRelativeTime, formatDateTime } from '../utils/helpers';
import UserStatusDialog from '../components/admin/UserStatusDialog';
import AdminEngagementPage from './AdminEngagementPage';
import AdminRevenuePage from './AdminRevenuePage';

const EMPTY_PLATFORM_STATS = {
  totalUsers: 0,
  activeUsers: 0,
  activeProjects: 0,
  fundedNotStartedProjects: 0,
  awaitingDepositProjects: 0,
  totalProjects: 0,
  completedProjects: 0,
  totalRevenue: 0,
  escrowHoldings: 0,
  userGrowthRate: 0,
  projectCompletionRate: 0,
  verificationRate: 0
};

const AdminDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Map URL paths to tab indices
  const getTabFromPath = (pathname) => {
    if (pathname.includes('/admin/users')) return 1;
    if (pathname.includes('/admin/settings')) return 2;
    if (pathname.includes('/admin/security')) return 3;
    if (pathname.includes('/admin/analytics')) return 4;
    if (pathname.includes('/admin/financial')) return 5;
    if (pathname.includes('/admin/content')) return 6;
    if (pathname.includes('/admin/withdrawals')) return 7;
    if (pathname.includes('/admin/refunds')) return 8;
    if (pathname.includes('/admin/engagement')) return 9;
    if (pathname.includes('/admin/revenue')) return 10;
    return 0; // default to Overview tab
  };

  const [activeTab, setActiveTab] = useState(getTabFromPath(location.pathname));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // ---- Real data state per domain ----
  const [overview, setOverview] = useState({
    platformStats: EMPTY_PLATFORM_STATS,
    usersByRole: {},
    projectsByStatus: {},
    verificationsByStatus: {},
    recentUsers: [],
    recentProjects: [],
    generatedAt: null
  });
  const [usersData, setUsersData] = useState([]);
  const [usersPagination, setUsersPagination] = useState({ total: 0, limit: 10, offset: 0 });
  const [usersFilters, setUsersFilters] = useState({ search: '', role: '', status: '' });
  const [analyticsKpis, setAnalyticsKpis] = useState(null);
  const [analyticsCharts, setAnalyticsCharts] = useState(null);
  const [analyticsRealtime, setAnalyticsRealtime] = useState(null);
  const [financialDistributions, setFinancialDistributions] = useState([]);
  const [financialWithdrawals, setFinancialWithdrawals] = useState([]);
  const [securityData, setSecurityData] = useState({
    summary: { totalAlerts: 0, activeMonitoring: 0, totalViolations: 0, highRiskUsers: 0 },
    alerts: [],
    riskUsers: []
  });
  const [securityNotice, setSecurityNotice] = useState('');
  const [contentItems, setContentItems] = useState([]);
  const [contentFilters, setContentFilters] = useState({ type: '', status: '' });
  const [contentDialogOpen, setContentDialogOpen] = useState(false);
  const [contentForm, setContentForm] = useState({ id: null, type: 'announcement', title: '', excerpt: '', body: '', status: 'draft', featuredImageUrl: '' });
  const [contentSaving, setContentSaving] = useState(false);
  const [statusDialog, setStatusDialog] = useState({ open: false, users: [], mode: 'suspended' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null, confirmText: '' });
  const [deleting, setDeleting] = useState(false);

  // Update tab when URL changes
  useEffect(() => {
    setActiveTab(getTabFromPath(location.pathname));
  }, [location.pathname]);

  // Handle tab change - update URL
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    const tabPaths = [
      '/admin/dashboard',
      '/admin/users',
      '/admin/settings',
      '/admin/security',
      '/admin/analytics',
      '/admin/financial',   // index 5: Financial Management
      '/admin/content',     // index 6: Content Management
      '/admin/withdrawals', // index 7: Withdrawals
      '/admin/refunds',     // index 8: Client refunds
      '/admin/engagement',  // index 9: Contractor->client engagement
      '/admin/revenue'      // index 10: Platform revenue
    ];
    navigate(tabPaths[newValue]);
  };

  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [systemHealth, setSystemHealth] = useState({
    status: 'unknown',
    uptime: '—',
    responseTime: '—',
    databaseStatus: 'unknown',
    activeConnections: 0,
    memoryUsagePct: 0,
    lastUpdated: new Date().toISOString()
  });

  // ---- Fetch helpers per tab ----
  const fetchSystemHealth = React.useCallback(async () => {
    try {
      const data = await adminService.getSystemHealth();
      setSystemHealth(data);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load system health');
    }
  }, []);

  const fetchOverview = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminService.getOverview();
      setOverview({
        platformStats: { ...EMPTY_PLATFORM_STATS, ...(data.platformStats || {}) },
        usersByRole: data.usersByRole || {},
        projectsByStatus: data.projectsByStatus || {},
        verificationsByStatus: data.verificationsByStatus || {},
        recentUsers: data.recentUsers || [],
        recentProjects: data.recentProjects || [],
        generatedAt: data.generatedAt
      });
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load overview data');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUsers = React.useCallback(async () => {
    setLoading(true);
    try {
      const { users, pagination } = await adminService.getUsers({
        search: usersFilters.search || undefined,
        role: usersFilters.role || undefined,
        status: usersFilters.status || undefined,
        limit: rowsPerPage,
        offset: page * rowsPerPage
      });
      setUsersData(users || []);
      setUsersPagination(pagination || { total: 0, limit: rowsPerPage, offset: page * rowsPerPage });
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [usersFilters, page, rowsPerPage]);

  const fetchAnalytics = React.useCallback(async () => {
    setLoading(true);
    try {
      const [kpis, charts, realtime] = await Promise.all([
        adminService.getKpis(),
        adminService.getCharts(),
        adminService.getRealtime()
      ]);
      setAnalyticsKpis(kpis);
      setAnalyticsCharts(charts);
      setAnalyticsRealtime(realtime);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFinancial = React.useCallback(async () => {
    setLoading(true);
    try {
      const [distributions, withdrawals] = await Promise.all([
        adminService.getPaymentDistributions({ limit: 50 }),
        adminService.getWithdrawals({ limit: 50 })
      ]);
      setFinancialDistributions(Array.isArray(distributions) ? distributions : []);
      setFinancialWithdrawals(withdrawals?.data || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load financial data');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSecurity = React.useCallback(async () => {
    setLoading(true);
    setSecurityNotice('');
    let dashboard = null;
    let alertsResult = null;
    try {
      dashboard = await adminService.getSecurityDashboard();
    } catch (err) {
      setSecurityNotice('Security monitoring data is currently unavailable.');
    }
    try {
      alertsResult = await adminService.getSecurityAlerts({ limit: 25 });
    } catch (err) {
      // non-blocking; dashboard may still have alerts
    }
    setSecurityData({
      summary: dashboard?.summary || { totalAlerts: 0, activeMonitoring: 0, totalViolations: 0, highRiskUsers: 0 },
      alerts: alertsResult?.alerts || dashboard?.alerts || [],
      riskUsers: dashboard?.riskUsers || []
    });
    setLoading(false);
  }, []);

  const fetchContent = React.useCallback(async () => {
    setLoading(true);
    try {
      const { content } = await adminService.getContent({
        type: contentFilters.type || undefined,
        status: contentFilters.status || undefined,
        limit: 50
      });
      setContentItems(content || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load content');
    } finally {
      setLoading(false);
    }
  }, [contentFilters]);

  // Overview + system health on mount
  useEffect(() => {
    fetchOverview();
    fetchSystemHealth();
  }, [fetchOverview, fetchSystemHealth]);

  // Poll real system health every 30s
  useEffect(() => {
    const interval = setInterval(() => {
      fetchSystemHealth();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchSystemHealth]);

  // Fetch active tab's data when it becomes active or its inputs change
  useEffect(() => {
    if (activeTab === 0) fetchOverview();
    else if (activeTab === 1) fetchUsers();
    else if (activeTab === 3) fetchSecurity();
    else if (activeTab === 4) fetchAnalytics();
    else if (activeTab === 5) fetchFinancial();
    else if (activeTab === 6) fetchContent();
  }, [activeTab, fetchOverview, fetchUsers, fetchSecurity, fetchAnalytics, fetchFinancial, fetchContent]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      if (activeTab === 0) {
        await Promise.all([fetchOverview(), fetchSystemHealth()]);
      } else if (activeTab === 1) {
        await fetchUsers();
      } else if (activeTab === 3) {
        await fetchSecurity();
      } else if (activeTab === 4) {
        await fetchAnalytics();
      } else if (activeTab === 5) {
        await fetchFinancial();
      } else if (activeTab === 6) {
        await fetchContent();
      } else {
        await fetchSystemHealth();
      }
      setSuccess('Data refreshed successfully');
    } catch (err) {
      setError('Failed to refresh data');
    } finally {
      setRefreshing(false);
    }
  };

  // User status / role actions
  const handleSetUserStatus = async (userId, status) => {
    try {
      await adminService.setUserStatus(userId, status);
      setSuccess(`User ${status === 'active' ? 'activated' : status}`);
      fetchUsers();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to update user status');
    }
  };

  const openStatusDialog = (targets, mode = 'suspended') => {
    setStatusDialog({
      open: true,
      mode,
      users: targets.map((u) => ({
        id: u.id,
        name: u.firstName ? `${u.firstName} ${u.lastName || ''}`.trim() : (u.email || u.id),
      })),
    });
  };

  const handleConfirmStatusChange = async ({ status, reason, suspendedUntil }) => {
    const ids = statusDialog.users.map((u) => u.id);
    await Promise.all(ids.map((id) => adminService.setUserStatus(id, status, { reason, suspendedUntil })));
    setSuccess(`${status === 'banned' ? 'Banned' : 'Suspended'} ${ids.length} user(s)`);
    fetchUsers();
  };

  const openDeleteDialog = (user) => {
    setError('');
    setDeleteDialog({ open: true, user, confirmText: '' });
  };

  const confirmDeleteUser = async () => {
    if (!deleteDialog.user) return;
    setDeleting(true);
    setError('');
    try {
      const res = await adminService.deleteUser(deleteDialog.user.id);
      setSuccess(res.message || `Deleted ${deleteDialog.user.email}`);
      setDeleteDialog({ open: false, user: null, confirmText: '' });
      fetchUsers();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const handleSetUserRole = async (userId, role) => {
    try {
      await adminService.setUserRole(userId, role);
      setSuccess(`User role set to ${role}`);
      fetchUsers();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to update user role');
    }
  };

  const handleResolveAlert = async (alertId) => {
    try {
      await adminService.resolveAlert(alertId, { resolution: 'Resolved by admin' });
      setSuccess('Alert resolved');
      fetchSecurity();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to resolve alert');
    }
  };

  const handleSaveContent = async () => {
    setContentSaving(true);
    try {
      const payload = {
        type: contentForm.type,
        title: contentForm.title,
        excerpt: contentForm.excerpt,
        body: contentForm.body,
        status: contentForm.status,
        featuredImageUrl: contentForm.featuredImageUrl || undefined
      };
      if (contentForm.id) {
        await adminService.updateContent(contentForm.id, payload);
        setSuccess('Content updated');
      } else {
        await adminService.createContent(payload);
        setSuccess('Content created');
      }
      setContentDialogOpen(false);
      fetchContent();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save content');
    } finally {
      setContentSaving(false);
    }
  };

  const handleBulkAction = (action, selectedItems) => {
    setSuccess(`${action} applied to ${selectedItems.length} items`);
  };

  const handleExportData = (type) => {
    setSuccess(`Exporting ${type} data...`);
    // Implement actual export logic
  };

  // Server-driven users list (search/filter/pagination drive the API)
  const filteredUsers = usersData;
  const paginatedUsers = usersData;
  const usersTotal = usersPagination?.total || 0;

  const getStatusChip = (status) => {
    const statusConfig = {
      active: { color: 'success', label: 'Active' },
      pending: { color: 'warning', label: 'Pending' },
      suspended: { color: 'error', label: 'Suspended' },
      banned: { color: 'error', label: 'Banned' },
      in_progress: { color: 'primary', label: 'In Progress' },
      completed: { color: 'success', label: 'Completed' },
      planning: { color: 'default', label: 'Planning' }
    };
    
    const config = statusConfig[status] || { color: 'default', label: status };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const getSeverityColor = (severity) => {
    const colors = {
      high: 'error',
      medium: 'warning', 
      low: 'info'
    };
    return colors[severity] || 'default';
  };

  // Enhanced Overview Tab with real-time monitoring
  const OverviewTab = () => (
    <Grid container spacing={3}>
      {/* Platform Statistics */}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Platform Overview
          </Typography>
          <Box>
            <Button 
              startIcon={refreshing ? <CircularProgress size={16} /> : <Refresh />} 
              onClick={handleRefresh}
              disabled={refreshing}
              size="small"
              sx={{ mr: 1 }}
            >
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
            <Button 
              startIcon={<Download />} 
              onClick={() => handleExportData('overview')}
              variant="outlined"
              size="small"
            >
              Export
            </Button>
          </Box>
        </Box>
      </Grid>
      
      {/* Enhanced Stats Cards with Trends */}
      <Grid item xs={12} sm={6} md={3}>
        <Card sx={{ position: 'relative', overflow: 'visible' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                <People />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {(overview.platformStats.totalUsers || 0).toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Users
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUp sx={{ color: 'success.main', fontSize: 16, mr: 0.5 }} />
                <Typography variant="caption" color="success.main">
                  +{overview.platformStats.userGrowthRate || 0}%
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                vs last month
              </Typography>
            </Box>
          </CardContent>
          <Box 
            sx={{ 
              position: 'absolute', 
              top: 8, 
              right: 8,
              bgcolor: 'success.main',
              color: 'white',
              borderRadius: '50%',
              width: 8,
              height: 8
            }} 
          />
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card sx={{ position: 'relative', overflow: 'visible' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ bgcolor: 'secondary.main', mr: 2 }}>
                <Business />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'secondary.main' }}>
                  {overview.platformStats.activeProjects || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Projects
                </Typography>
              </Box>
            </Box>
            {/* Active = a stage has started. Accepted jobs that haven't got
                there yet are counted here, not as active. */}
            <Typography variant="caption" color="text.secondary" component="div">
              Work started on a stage
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              {overview.platformStats.fundedNotStartedProjects || 0} funded, not started
              {' · '}
              {overview.platformStats.awaitingDepositProjects || 0} awaiting deposit
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card sx={{ position: 'relative', overflow: 'visible' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                <MonetizationOn />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                  {formatCurrency(overview.platformStats.totalRevenue || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Revenue
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AccountBalance sx={{ color: 'primary.main', fontSize: 16, mr: 0.5 }} />
                <Typography variant="caption" color="primary.main">
                  {formatCurrency(overview.platformStats.escrowHoldings || 0)}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                in escrow
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card sx={{ position: 'relative', overflow: 'visible' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                <VerifiedUser />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.main' }}>
                  {overview.platformStats.verificationRate || 0}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Verification Rate
                </Typography>
              </Box>
            </Box>
            <LinearProgress
              variant="determinate"
              value={overview.platformStats.verificationRate || 0}
              sx={{ mb: 1 }}
            />
            <Typography variant="caption" color="text.secondary">
              {overview.platformStats.verificationRate || 0}% of users verified
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Real-time System Health */}
      <Grid item xs={12} md={4}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                System Health
              </Typography>
              <Chip
                label={(systemHealth.status || 'unknown').toUpperCase()}
                color={systemHealth.status === 'healthy' ? 'success' : systemHealth.status === 'unknown' ? 'default' : 'error'}
                size="small"
              />
            </Box>

            <Box sx={{ display: 'grid', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Uptime</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                  {systemHealth.uptime || '—'}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Response Time</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                  {systemHealth.responseTime || '—'}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Database</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: systemHealth.databaseStatus === 'connected' ? 'success.main' : 'error.main' }}>
                  {(systemHealth.databaseStatus || 'unknown')}{systemHealth.databaseLatencyMs != null ? ` (${systemHealth.databaseLatencyMs}ms)` : ''}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Memory Usage</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {systemHealth.memoryUsagePct != null ? `${systemHealth.memoryUsagePct}%` : '—'}
                  {systemHealth.memoryUsedMb != null ? ` (${systemHealth.memoryUsedMb}/${systemHealth.memoryTotalMb}MB)` : ''}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Active Connections</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {(systemHealth.activeConnections || 0).toLocaleString()}
                </Typography>
              </Box>
            </Box>
            
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              Last updated: {formatRelativeTime(systemHealth.lastUpdated)}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Recent Users */}
      <Grid item xs={12} md={8}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Recent Users
              </Typography>
            </Box>

            {overview.recentUsers.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No recent users.
              </Typography>
            ) : (
              <List>
                {overview.recentUsers.slice(0, 6).map((u, index) => (
                  <React.Fragment key={u.id}>
                    <ListItem sx={{ borderRadius: 1, mb: 0.5 }}>
                      <ListItemIcon>
                        <Avatar src={u.profileImageUrl} sx={{ width: 36, height: 36 }}>
                          {u.firstName && u.firstName[0]}{u.lastName && u.lastName[0]}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email}
                        secondary={`${u.email} • ${u.role}${u.createdAt ? ' • ' + formatRelativeTime(u.createdAt) : ''}`}
                      />
                      <Chip
                        label={(u.status || 'active').toUpperCase()}
                        size="small"
                        color={u.status === 'active' ? 'success' : u.status === 'pending' ? 'warning' : (u.status === 'suspended' || u.status === 'banned') ? 'error' : 'default'}
                      />
                    </ListItem>
                    {index < overview.recentUsers.slice(0, 6).length - 1 && <Divider component="li" />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Quick Actions */}
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Quick Actions
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button variant="outlined" startIcon={<Add />} fullWidth>
                Add New Admin
              </Button>
              <Button variant="outlined" startIcon={<Download />} fullWidth>
                Export User Data
              </Button>
              <Button variant="outlined" startIcon={<Assessment />} fullWidth>
                Generate Report
              </Button>
              <Button variant="outlined" startIcon={<Settings />} fullWidth>
                System Settings
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Admin Module Cards */}
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 2, mb: 2 }}>
          Admin Modules
        </Typography>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }} onClick={() => window.location.href = '/admin/reviews'}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Avatar sx={{ bgcolor: 'warning.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
              <Report />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Review Moderation
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage flagged reviews
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }} onClick={() => window.location.href = '/admin/appeals'}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Avatar sx={{ bgcolor: 'error.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
              <Group />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Review Appeals
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Handle review disputes
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }} onClick={() => window.location.href = '/admin/verification'}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Avatar sx={{ bgcolor: 'success.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
              <VerifiedUser />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Verification Review
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review verification apps
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }} onClick={() => window.location.href = '/admin/applications'}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Avatar sx={{ bgcolor: 'warning.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
              <VerifiedUser />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Applications</Typography>
            <Typography variant="body2" color="text.secondary">Verify new contractors &amp; verifiers</Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }} onClick={() => window.location.href = '/admin/conversations'}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Avatar sx={{ bgcolor: 'info.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
              <NotificationsActive />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Conversation Monitor
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Monitor conversations
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }} onClick={() => window.location.href = '/admin/engagement'}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Avatar sx={{ bgcolor: 'success.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
              <TrendingUp />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Engagement
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Contractor outreach to clients
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }} onClick={() => window.location.href = '/admin/security-console'}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Avatar sx={{ bgcolor: 'secondary.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
              <Security />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Security Console
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Security management
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  // Enhanced Users Tab with Advanced Management
  const UsersTab = () => {
    const [verificationFilter, setVerificationFilter] = useState('');
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const statusFilter = usersFilters.status;
    const roleFilter = usersFilters.role;

    const handleSelectAllUsers = (event) => {
      if (event.target.checked) {
        setSelectedUsers(filteredUsers.map(user => user.id));
      } else {
        setSelectedUsers([]);
      }
    };

    const handleUserSelect = (userId, checked) => {
      setSelectedUsers(prev => {
        if (checked) {
          return [...prev, userId];
        } else {
          return prev.filter(id => id !== userId);
        }
      });
    };

    const handleBulkAction = async (action, userIds) => {
      try {
        if (action === 'suspend') {
          const targets = filteredUsers.filter((u) => userIds.includes(u.id));
          openStatusDialog(targets);
        } else if (action === 'verify' || action === 'activate') {
          await Promise.all(userIds.map((id) => adminService.setUserStatus(id, 'active')));
          setSuccess(`Activated ${userIds.length} user(s)`);
          fetchUsers();
        } else {
          setSuccess(`${action} action applied to ${userIds.length} user(s)`);
        }
      } catch (err) {
        setError(err?.response?.data?.message || `Failed to ${action} users`);
      }
      setSelectedUsers([]);
    };

    const handleViewUser = (user) => {
      setSelectedUser(user);
      setUserDialogOpen(true);
    };

    const handleEditUser = (user) => {
      setSelectedUser(user);
      setUserDialogOpen(true);
    };

    return (
      <Grid container spacing={3}>
        {/* Advanced Search and Filter Bar */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setPage(0);
                      setUsersFilters((prev) => ({ ...prev, search: searchTerm }));
                    }
                  }}
                  InputProps={{
                    startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => { setPage(0); setUsersFilters((prev) => ({ ...prev, status: e.target.value })); }}
                    label="Status"
                  >
                    <MenuItem value="">All Status</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="suspended">Suspended</MenuItem>
                    <MenuItem value="banned">Banned</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={roleFilter}
                    onChange={(e) => { setPage(0); setUsersFilters((prev) => ({ ...prev, role: e.target.value })); }}
                    label="Role"
                  >
                    <MenuItem value="">All Roles</MenuItem>
                    <MenuItem value="client">Client</MenuItem>
                    <MenuItem value="contractor">Contractor</MenuItem>
                    <MenuItem value="verifier">Verifier</MenuItem>
                    <MenuItem value="project_manager">Project Manager</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Verification</InputLabel>
                  <Select value={verificationFilter} onChange={(e) => setVerificationFilter(e.target.value)} label="Verification">
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="verified">Verified</MenuItem>
                    <MenuItem value="unverified">Unverified</MenuItem>
                    <MenuItem value="pending_verification">Pending</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  variant="outlined"
                  startIcon={<FilterList />}
                  onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                  fullWidth
                >
                  More Filters
                </Button>
              </Grid>
            </Grid>
            
            {/* Advanced Filters Accordion */}
            {showAdvancedFilters && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Join Date From"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Join Date To"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Last Login From"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Country</InputLabel>
                      <Select label="Country">
                        <MenuItem value="">All Countries</MenuItem>
                        <MenuItem value="ZW">Zimbabwe</MenuItem>
                        <MenuItem value="ZA">South Africa</MenuItem>
                        <MenuItem value="BW">Botswana</MenuItem>
                        <MenuItem value="ZM">Zambia</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Bulk Actions Bar */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, mb: 2, bgcolor: selectedUsers.length > 0 ? 'action.selected' : 'background.paper' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Checkbox
                  checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                  indeterminate={selectedUsers.length > 0 && selectedUsers.length < filteredUsers.length}
                  onChange={handleSelectAllUsers}
                />
                <Typography variant="body2">
                  {selectedUsers.length > 0 ? `${selectedUsers.length} user(s) selected` : `${usersTotal} user(s) found`}
                </Typography>
              </Box>
              
              {selectedUsers.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" startIcon={<CheckCircle />} onClick={() => handleBulkAction('verify', selectedUsers)}>
                    Verify
                  </Button>
                  <Button size="small" startIcon={<Block />} onClick={() => handleBulkAction('suspend', selectedUsers)}>
                    Suspend
                  </Button>
                  <Button size="small" startIcon={<Email />} onClick={() => handleBulkAction('email', selectedUsers)}>
                    Send Email
                  </Button>
                  <Button size="small" startIcon={<GetApp />} onClick={() => handleBulkAction('export', selectedUsers)}>
                    Export
                  </Button>
                  <Button size="small" color="error" startIcon={<Delete />} onClick={() => handleBulkAction('delete', selectedUsers)}>
                    Delete
                  </Button>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Enhanced Users Table */}
        <Grid item xs={12}>
          <Card>
            <CardContent sx={{ p: 0 }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                          indeterminate={selectedUsers.length > 0 && selectedUsers.length < filteredUsers.length}
                          onChange={handleSelectAllUsers}
                        />
                      </TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Verification</TableCell>
                      <TableCell>Last Login</TableCell>
                      <TableCell>KYC</TableCell>
                      <TableCell>Joined</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedUsers.map((user) => (
                      <TableRow 
                        key={user.id} 
                        hover 
                        selected={selectedUsers.includes(user.id)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedUsers.includes(user.id)}
                            onChange={(e) => handleUserSelect(user.id, e.target.checked)}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Badge
                              overlap="circular"
                              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                              badgeContent={
                                user.status === 'active' ? (
                                  <Avatar sx={{ width: 12, height: 12, bgcolor: 'success.main' }} />
                                ) : user.status === 'pending' ? (
                                  <Avatar sx={{ width: 12, height: 12, bgcolor: 'warning.main' }} />
                                ) : (
                                  <Avatar sx={{ width: 12, height: 12, bgcolor: 'error.main' }} />
                                )
                              }
                            >
                              <Avatar
                                src={user.profileImageUrl}
                                sx={{ width: 40, height: 40 }}
                              >
                                {user.firstName && user.firstName[0]}{user.lastName && user.lastName[0]}
                              </Avatar>
                            </Badge>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {user.firstName} {user.lastName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {user.email}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                  ID: {user.id.toString().slice(0, 8)}...
                                </Typography>
                                {user.isVerified && <CheckCircle sx={{ fontSize: 12, color: 'success.main' }} />}
                              </Box>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={user.role.toUpperCase()} 
                            size="small"
                            color={
                              user.role === 'admin' ? 'error' :
                              user.role === 'contractor' ? 'primary' :
                              user.role === 'verifier' ? 'secondary' :
                              user.role === 'quality_inspector' ? 'info' : 'default'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.status.toUpperCase()}
                            size="small"
                            color={
                              user.status === 'active' ? 'success' :
                              user.status === 'pending' ? 'warning' :
                              (user.status === 'suspended' || user.status === 'banned') ? 'error' : 'default'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {user.emailVerified && <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />}
                            {user.phoneVerified && <Verified sx={{ fontSize: 16, color: 'primary.main' }} />}
                            {user.documentVerified && <Assignment sx={{ fontSize: 16, color: 'info.main' }} />}
                            {!user.emailVerified && !user.phoneVerified && !user.documentVerified && (
                              <Chip label="UNVERIFIED" size="small" color="error" />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {user.lastLogin ? formatRelativeTime(user.lastLogin) : 'Never'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={(user.kycStatus || 'none').toUpperCase()}
                            size="small"
                            color={user.kycStatus === 'verified' || user.kycStatus === 'approved' ? 'success' : user.kycStatus === 'pending' ? 'warning' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {user.createdAt ? formatDate(user.createdAt) : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="View Details">
                              <IconButton size="small" onClick={() => handleViewUser(user)}>
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit User">
                              <IconButton size="small" onClick={() => handleEditUser(user)}>
                                <Edit />
                              </IconButton>
                            </Tooltip>
                            {user.status === 'suspended' || user.status === 'banned' ? (
                              <Tooltip title="Activate User">
                                <IconButton size="small" color="success" onClick={() => handleSetUserStatus(user.id, 'active')}>
                                  <CheckCircle />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Suspend or Ban User">
                                <IconButton size="small" color="error" onClick={() => openStatusDialog([user])}>
                                  <Block />
                                </IconButton>
                              </Tooltip>
                            )}
                            {user.role !== 'admin' && (
                              <Tooltip title="Delete User">
                                <IconButton size="small" color="error" onClick={() => openDeleteDialog(user)}>
                                  <Delete />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Enhanced Pagination */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {usersTotal === 0 ? 0 : (page * rowsPerPage) + 1} to {Math.min((page + 1) * rowsPerPage, usersTotal)} of {usersTotal} users
                </Typography>
                <TablePagination
                  component="div"
                  count={usersTotal}
                  page={page}
                  onPageChange={(e, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  // System Settings Tab
  const SystemSettingsTab = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Platform Configuration
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    User Registration
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Allow new users to register
                  </Typography>
                </Box>
                <Switch defaultChecked />
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Email Verification Required
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Require email verification for new accounts
                  </Typography>
                </Box>
                <Switch defaultChecked />
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Contractor Auto-Approval
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Automatically approve contractor registrations
                  </Typography>
                </Box>
                <Switch />
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Maintenance Mode
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Put platform in maintenance mode
                  </Typography>
                </Box>
                <Switch />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Payment Settings
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                label="Platform Fee (%)"
                type="number"
                defaultValue="5"
                size="small"
                fullWidth
              />
              
              <TextField
                label="Minimum Escrow Amount (USD)"
                type="number"
                defaultValue="100"
                size="small"
                fullWidth
              />
              
              <TextField
                label="Maximum Project Budget (USD)"
                type="number"
                defaultValue="1000000"
                size="small"
                fullWidth
              />
              
              <FormControl size="small" fullWidth>
                <InputLabel>Default Currency</InputLabel>
                <Select defaultValue="USD" label="Default Currency">
                  <MenuItem value="USD">USD - US Dollar</MenuItem>
                  <MenuItem value="ZWL">ZWL - Zimbabwean Dollar</MenuItem>
                  <MenuItem value="ZAR">ZAR - South African Rand</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Email & Notification Settings
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="SMTP Server"
                  defaultValue="smtp.eqaya.co.zw"
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="SMTP Port"
                  type="number"
                  defaultValue="587"
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="From Email"
                  defaultValue="noreply@eqaya.co.zw"
                  size="small"
                  fullWidth
                />
              </Grid>
            </Grid>
          </CardContent>
          <CardActions>
            <Button variant="contained" startIcon={<Settings />}>
              Save Settings
            </Button>
            <Button variant="outlined" startIcon={<Assessment />}>
              Test Email
            </Button>
          </CardActions>
        </Card>
      </Grid>
    </Grid>
  );

  // Enhanced Security & Audit Tab with Advanced Features
  const SecurityAuditTab = () => {
    const [securityTab, setSecurityTab] = useState(0);
    // TODO: Replace with real API call to /api/admin/security/ip-whitelist
    const [ipWhitelist, setIpWhitelist] = useState([]);

    const securityTabs = ['Security Events', 'IP Management', 'API Monitoring', 'Compliance', 'Threat Detection'];

    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                🔒 Advanced Security & Audit Management
              </Typography>
              
              <Tabs value={securityTab} onChange={(e, v) => setSecurityTab(v)} variant="scrollable" sx={{ mb: 3 }}>
                {securityTabs.map((tab, index) => (
                  <Tab key={index} label={tab} />
                ))}
              </Tabs>

              {/* Security Events */}
              {securityTab === 0 && (
                <Box>
                  {securityNotice && (
                    <Alert severity="info" sx={{ mb: 2 }}>{securityNotice}</Alert>
                  )}

                  {/* Summary cards */}
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6} md={3}>
                      <Card variant="outlined">
                        <CardContent sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" color="error.main" sx={{ fontWeight: 700 }}>
                            {securityData.summary.totalAlerts || 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">Total Alerts</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Card variant="outlined">
                        <CardContent sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" color="primary.main" sx={{ fontWeight: 700 }}>
                            {securityData.summary.activeMonitoring || 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">Active Monitoring</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Card variant="outlined">
                        <CardContent sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" color="warning.main" sx={{ fontWeight: 700 }}>
                            {securityData.summary.totalViolations || 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">Violations</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Card variant="outlined">
                        <CardContent sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" color="error.main" sx={{ fontWeight: 700 }}>
                            {securityData.summary.highRiskUsers || 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">High-Risk Users</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Alerts table */}
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Security Alerts</Typography>
                  <TableContainer sx={{ mb: 3 }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Type</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell>Severity</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Time</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {securityData.alerts.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6}>
                              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                                No security alerts.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          securityData.alerts.map((alert, index) => (
                            <TableRow key={alert.id || index} hover>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {alert.alert_type || alert.type || alert.event || 'Alert'}
                                </Typography>
                              </TableCell>
                              <TableCell>{alert.description || alert.message || '—'}</TableCell>
                              <TableCell>
                                <Chip
                                  label={(alert.severity || 'low').toUpperCase()}
                                  size="small"
                                  color={alert.severity === 'high' || alert.severity === 'critical' ? 'error' : alert.severity === 'medium' ? 'warning' : 'success'}
                                />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={(alert.status || 'open').toUpperCase()}
                                  size="small"
                                  color={alert.status === 'resolved' ? 'success' : 'default'}
                                />
                              </TableCell>
                              <TableCell>
                                {alert.created_at || alert.createdAt ? formatDateTime(alert.created_at || alert.createdAt) : '—'}
                              </TableCell>
                              <TableCell>
                                {alert.status !== 'resolved' && (
                                  <Button size="small" variant="outlined" onClick={() => handleResolveAlert(alert.id)}>
                                    Resolve
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* High-risk users */}
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>High-Risk Users</Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>User</TableCell>
                          <TableCell>Role</TableCell>
                          <TableCell align="right">Risk Score</TableCell>
                          <TableCell align="right">Violations</TableCell>
                          <TableCell align="right">Warnings</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {securityData.riskUsers.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5}>
                              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                                No high-risk users.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          securityData.riskUsers.map((ru, index) => (
                            <TableRow key={ru.id || index} hover>
                              <TableCell>{ru.email}</TableCell>
                              <TableCell>{ru.role}</TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={ru.overall_risk_score ?? 0}
                                  size="small"
                                  color={(ru.overall_risk_score || 0) > 70 ? 'error' : (ru.overall_risk_score || 0) > 40 ? 'warning' : 'default'}
                                />
                              </TableCell>
                              <TableCell align="right">{ru.violation_count ?? 0}</TableCell>
                              <TableCell align="right">{ru.warning_count ?? 0}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* IP Management */}
              {securityTab === 1 && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6">IP Whitelist Management</Typography>
                    <Button variant="contained" startIcon={<Add />}>
                      Add IP Range
                    </Button>
                  </Box>
                  
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>IP Address/Range</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Added Date</TableCell>
                          <TableCell>Last Access</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {ipWhitelist.map((entry) => (
                          <TableRow key={entry.id} hover>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                {entry.ip}
                              </Typography>
                            </TableCell>
                            <TableCell>{entry.description}</TableCell>
                            <TableCell>
                              <Chip 
                                label={entry.status.toUpperCase()} 
                                size="small"
                                color={entry.status === 'active' ? 'success' : 'default'}
                              />
                            </TableCell>
                            <TableCell>{formatDate(entry.added)}</TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                2 hours ago
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <IconButton size="small">
                                <Edit />
                              </IconButton>
                              <IconButton size="small" color="error">
                                <Block />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* API Monitoring */}
              {securityTab === 2 && (
                <Box>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <Card variant="outlined">
                        <CardContent sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" color="primary.main" sx={{ fontWeight: 700 }}>
                            2,547
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            API Requests Today
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Card variant="outlined">
                        <CardContent sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" color="error.main" sx={{ fontWeight: 700 }}>
                            12
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Rate Limit Violations
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Card variant="outlined">
                        <CardContent sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" color="success.main" sx={{ fontWeight: 700 }}>
                            99.8%
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            API Uptime
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Alert severity="info">
                        📊 Real-time API monitoring dashboard with endpoint performance metrics will be integrated here
                      </Alert>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Compliance */}
              {securityTab === 3 && (
                <Box>
                  <Grid container spacing={3}>
                    {/* TODO: Replace with real compliance data from API */}
                    {[].map((compliance, index) => (
                      <Grid item xs={12} md={6} key={index}>
                        <Card variant="outlined">
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {compliance.standard}
                              </Typography>
                              <Chip 
                                label={compliance.status.toUpperCase().replace('-', ' ')}
                                color={
                                  compliance.status === 'compliant' ? 'success' :
                                  compliance.status === 'in-progress' ? 'warning' : 'error'
                                }
                                size="small"
                              />
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              Last Audit: {formatDate(compliance.lastAudit)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Next Audit: {formatDate(compliance.nextAudit)}
                            </Typography>
                          </CardContent>
                          <CardActions>
                            <Button size="small">View Report</Button>
                            <Button size="small">Schedule Audit</Button>
                          </CardActions>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Threat Detection */}
              {securityTab === 4 && (
                <Box>
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    🚨 <strong>Active Threats Detected:</strong> 3 potential security incidents require attention
                  </Alert>
                  
                  <Grid container spacing={3}>
                    {/* TODO: Replace with real threat data from API */}
                    {[].map((threat, index) => (
                      <Grid item xs={12} key={index}>
                        <Alert 
                          severity={threat.severity === 'high' ? 'error' : threat.severity === 'medium' ? 'warning' : 'info'}
                          action={
                            <Box>
                              <Button size="small" color="inherit">Investigate</Button>
                              <Button size="small" color="inherit">Block</Button>
                            </Box>
                          }
                        >
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {threat.threat}
                          </Typography>
                          <Typography variant="body2">
                            Source: {threat.source} • Status: {threat.blocked ? 'Blocked' : 'Monitoring'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Detected: {formatDateTime(threat.detected)}
                          </Typography>
                        </Alert>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  // Platform Analytics Tab wired to /api/analytics
  const PlatformAnalyticsTab = () => {
    const kpis = analyticsKpis;
    const charts = analyticsCharts;
    const realtime = analyticsRealtime;

    const realtimeCards = realtime ? [
      { label: 'Active Users', value: realtime.activeUsers ?? 0 },
      { label: 'Ongoing Projects', value: realtime.ongoingProjects ?? 0 },
      { label: 'Recent Transactions', value: (realtime.recentTransactions || []).length },
      { label: 'Alerts', value: realtime.alertCount ?? 0 }
    ] : [];

    return (
      <Grid container spacing={3}>
        {/* Analytics Header */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, mb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Platform Analytics
              </Typography>
              <Button
                variant="outlined"
                startIcon={refreshing ? <CircularProgress size={16} /> : <Refresh />}
                onClick={handleRefresh}
                disabled={refreshing}
              >
                Refresh
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Realtime metric cards */}
        <Grid item xs={12}>
          <Grid container spacing={3}>
            {realtimeCards.length === 0 ? (
              <Grid item xs={12}>
                <Alert severity="info">No realtime data available.</Alert>
              </Grid>
            ) : realtimeCards.map((card, index) => (
              <Grid item xs={6} md={3} key={card.label}>
                <Card sx={{ textAlign: 'center' }}>
                  <CardContent>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: index % 2 === 0 ? 'primary.main' : 'success.main' }}>
                      {card.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {card.label}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* KPI cards */}
        <Grid item xs={12}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="primary.main" sx={{ fontWeight: 700 }}>
                    {kpis?.totalProjects ?? 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Total Projects</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="success.main" sx={{ fontWeight: 700 }}>
                    {kpis ? `${kpis.completionRate ?? 0}%` : '0%'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Completion Rate</Typography>
                  <LinearProgress variant="determinate" value={kpis?.completionRate || 0} sx={{ mt: 1, height: 6, borderRadius: 3 }} />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="success.main" sx={{ fontWeight: 700 }}>
                    {formatCurrency(kpis?.totalValue || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Total Value</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="primary.main" sx={{ fontWeight: 700 }}>
                    {formatCurrency(kpis?.averageProjectValue || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Avg Project Value</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* Project Trends */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Project Trends
              </Typography>
              <Box sx={{ height: 320 }}>
                {charts?.projectTrends?.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={charts.projectTrends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      <Line type="monotone" dataKey="projects" stroke="#1976d2" name="Projects" />
                      <Line type="monotone" dataKey="completed" stroke="#2e7d32" name="Completed" />
                      <Line type="monotone" dataKey="active" stroke="#ed6c02" name="Active" />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="text.secondary">No project trend data.</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Revenue Chart */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Revenue & Escrow
              </Typography>
              <Box sx={{ height: 320 }}>
                {charts?.revenueChart?.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={charts.revenueChart}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                      <Area type="monotone" dataKey="revenue" stroke="#2e7d32" fill="#2e7d3233" name="Revenue" />
                      <Area type="monotone" dataKey="escrowVolume" stroke="#1976d2" fill="#1976d233" name="Escrow Volume" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="text.secondary">No revenue data.</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* User Activity */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                User Activity
              </Typography>
              <Box sx={{ height: 320 }}>
                {charts?.userActivity?.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={charts.userActivity}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      <Bar dataKey="registrations" fill="#1976d2" name="Registrations" />
                      <Bar dataKey="logins" fill="#9c27b0" name="Logins" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="text.secondary">No user activity data.</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Project Status Pie */}
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Project Status Distribution
              </Typography>
              <Box sx={{ height: 320 }}>
                {charts?.projectStatus?.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={charts.projectStatus} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label>
                        {charts.projectStatus.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color || ['#1976d2', '#2e7d32', '#ed6c02', '#9c27b0', '#d32f2f'][index % 5]} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="text.secondary">No status data.</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Monthly Performance */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Monthly Performance
              </Typography>
              <Box sx={{ height: 320 }}>
                {charts?.monthlyPerformance?.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={charts.monthlyPerformance}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      <Line type="monotone" dataKey="efficiency" stroke="#1976d2" name="Efficiency" />
                      <Line type="monotone" dataKey="onTime" stroke="#2e7d32" name="On-Time" />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="text.secondary">No performance data.</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Regional Distribution */}
        <Grid item xs={12}>
          <RegionalDistributionPanel />
        </Grid>
      </Grid>
    );
  };

  // Financial Management Tab wired to payment distributions + withdrawals
  const FinancialManagementTab = () => {
    const totalDistributed = financialDistributions.reduce((sum, d) => sum + Number(d.total_amount || 0), 0);
    const completedDistributions = financialDistributions.filter((d) => d.status === 'completed').length;
    const pendingWithdrawals = financialWithdrawals.filter((w) => w.status === 'pending').length;
    const pendingWithdrawalAmount = financialWithdrawals
      .filter((w) => w.status === 'pending')
      .reduce((sum, w) => sum + Number(w.amount || 0), 0);

    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Grid container spacing={3} sx={{ mb: 1 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Avatar sx={{ bgcolor: 'success.main', mx: 'auto', mb: 2 }}>
                    <MonetizationOn />
                  </Avatar>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: 'success.main' }}>
                    {formatCurrency(totalDistributed)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Distributed
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mx: 'auto', mb: 2 }}>
                    <AccountBalance />
                  </Avatar>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {completedDistributions}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completed Distributions
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Avatar sx={{ bgcolor: 'warning.main', mx: 'auto', mb: 2 }}>
                    <Schedule />
                  </Avatar>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: 'warning.main' }}>
                    {formatCurrency(pendingWithdrawalAmount)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending Withdrawals
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Avatar sx={{ bgcolor: 'error.main', mx: 'auto', mb: 2 }}>
                    <ErrorOutline />
                  </Avatar>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: 'error.main' }}>
                    {pendingWithdrawals}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Withdrawals Awaiting Action
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* Payment Distributions */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Payment Distributions
              </Typography>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Project</TableCell>
                      <TableCell>Milestone</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Gateway</TableCell>
                      <TableCell>Recipients</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {financialDistributions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                            No payment distributions.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      financialDistributions.map((d) => (
                        <TableRow key={d.id} hover>
                          <TableCell>{d.projectName || d.projectId || '—'}</TableCell>
                          <TableCell>{d.milestoneName || d.milestone_id || '—'}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{formatCurrency(d.total_amount || 0, d.currency)}</TableCell>
                          <TableCell>{d.payment_gateway || '—'}</TableCell>
                          <TableCell>{d.recipientCount ?? 0}</TableCell>
                          <TableCell>
                            <Chip
                              label={(d.status || 'pending').toUpperCase()}
                              color={d.status === 'completed' ? 'success' : d.status === 'failed' ? 'error' : 'warning'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{d.createdAt ? formatDate(d.createdAt) : '—'}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Withdrawals summary */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Withdrawal Requests
              </Typography>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Method</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {financialWithdrawals.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                            No withdrawal requests.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      financialWithdrawals.map((w) => (
                        <TableRow key={w.id} hover>
                          <TableCell>{w.full_name || w.email || '—'}</TableCell>
                          <TableCell>{w.user_role || '—'}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{formatCurrency(w.amount || 0, w.currency)}</TableCell>
                          <TableCell>{w.payment_method || '—'}</TableCell>
                          <TableCell>
                            <Chip
                              label={(w.status || 'pending').toUpperCase()}
                              color={w.status === 'completed' ? 'success' : w.status === 'failed' || w.status === 'rejected' ? 'error' : 'warning'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
            <CardActions>
              <Button variant="outlined" startIcon={<Payment />} onClick={() => handleTabChange(null, 7)}>
                Manage Withdrawals
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>
    );
  };

  // Content Management System Tab — real CRUD against /api/content/admin
  const ContentManagementTab = () => {
    const handleNew = () => {
      setContentForm({ id: null, type: 'announcement', title: '', excerpt: '', body: '', status: 'draft', featuredImageUrl: '' });
      setContentDialogOpen(true);
    };

    const handleEditContent = (item) => {
      setContentForm({
        id: item.id,
        type: item.type || 'announcement',
        title: item.title || '',
        excerpt: item.excerpt || '',
        body: item.body || '',
        status: item.status || 'draft',
        featuredImageUrl: item.featured_image_url || ''
      });
      setContentDialogOpen(true);
    };

    const handleDeleteContent = async (id) => {
      try {
        await adminService.deleteContent(id);
        setSuccess('Content deleted');
        fetchContent();
      } catch (err) {
        setError(err?.response?.data?.message || 'Failed to delete content');
      }
    };

    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Content Management
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={contentFilters.type}
                      label="Type"
                      onChange={(e) => setContentFilters((prev) => ({ ...prev, type: e.target.value }))}
                    >
                      <MenuItem value="">All Types</MenuItem>
                      <MenuItem value="announcement">Announcement</MenuItem>
                      <MenuItem value="blog">Blog</MenuItem>
                      <MenuItem value="help">Help</MenuItem>
                      <MenuItem value="media">Media</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={contentFilters.status}
                      label="Status"
                      onChange={(e) => setContentFilters((prev) => ({ ...prev, status: e.target.value }))}
                    >
                      <MenuItem value="">All Status</MenuItem>
                      <MenuItem value="draft">Draft</MenuItem>
                      <MenuItem value="published">Published</MenuItem>
                      <MenuItem value="archived">Archived</MenuItem>
                    </Select>
                  </FormControl>
                  <Button variant="contained" startIcon={<Add />} onClick={handleNew}>
                    Create
                  </Button>
                </Box>
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Author</TableCell>
                      <TableCell>Updated</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {contentItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6}>
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                            No content items.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      contentItems.map((item) => (
                        <TableRow key={item.id} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.title}</Typography>
                            {item.excerpt && (
                              <Typography variant="caption" color="text.secondary">{item.excerpt}</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip label={(item.type || '').toUpperCase()} size="small" />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={(item.status || 'draft').toUpperCase()}
                              size="small"
                              color={item.status === 'published' ? 'success' : item.status === 'archived' ? 'default' : 'warning'}
                            />
                          </TableCell>
                          <TableCell>{item.author_name || '—'}</TableCell>
                          <TableCell>{item.updated_at ? formatDate(item.updated_at) : '—'}</TableCell>
                          <TableCell>
                            <IconButton size="small" onClick={() => handleEditContent(item)}>
                              <Edit />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDeleteContent(item.id)}>
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const tabs = [
    { label: 'Overview', icon: <Dashboard />, component: <OverviewTab /> },
    { label: 'Users', icon: <People />, component: <UsersTab /> },
    { label: 'System Settings', icon: <Settings />, component: <SystemSettingsTab /> },
    { label: 'Security & Audit', icon: <Security />, component: <SecurityAuditTab /> },
    { label: 'Analytics', icon: <Analytics />, component: <PlatformAnalyticsTab /> },
    { label: 'Financial Management', icon: <AccountBalance />, component: <FinancialManagementTab /> },
    { label: 'Content Management', icon: <Settings />, component: <ContentManagementTab /> },
    { label: 'Withdrawals', icon: <Payment />, component: <AdminWithdrawals /> },
    { label: 'Refunds', icon: <Payment />, component: <AdminRefunds /> },
    { label: 'Engagement', icon: <TrendingUp />, component: <AdminEngagementPage embedded /> },
    { label: 'Revenue', icon: <AttachMoney />, component: <AdminRevenuePage embedded /> }
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Enhanced Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 48, height: 48 }}>
              <AdminPanelSettings />
            </Avatar>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Badge badgeContent={securityData.summary.totalAlerts || 0} color="error">
              <IconButton>
                <NotificationsActive />
              </IconButton>
            </Badge>
            <Button variant="outlined" startIcon={<Settings />}>
              Settings
            </Button>
          </Box>
        </Box>
        
        {/* Quick Stats Banner */}
        <Paper sx={{ p: 2, bgcolor: 'primary.main', color: 'white', borderRadius: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {overview.platformStats.totalUsers || 0}
                </Typography>
                <Typography variant="caption">Total Users</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {overview.platformStats.activeProjects || 0}
                </Typography>
                <Typography variant="caption">Active Projects</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {formatCurrency(overview.platformStats.totalRevenue || 0)}
                </Typography>
                <Typography variant="caption">Total Revenue</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {overview.platformStats.verificationRate || 0}%
                </Typography>
                <Typography variant="caption">Verification Rate</Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* Navigation Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="admin tabs"
          variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              icon={tab.icon}
              label={tab.label}
              iconPosition="start"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
          ))}
        </Tabs>
      </Box>

      {/* Tab Content */}
      {tabs[activeTab].component}

      {/* Enhanced User Details Dialog */}
      <Dialog open={userDialogOpen} onClose={() => setUserDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogCloseButton onClick={() => setUserDialogOpen(false)} />
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar>
              {selectedUser?.firstName && selectedUser.lastName ? `${selectedUser.firstName[0]}${selectedUser.lastName[0]}` : 'A'}
            </Avatar>
            <Box>
              <Typography variant="h6">{selectedUser?.firstName} {selectedUser?.lastName}</Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedUser?.email}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedUser && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>Account Information</Typography>
                <List dense>
                  <ListItem>
                    <ListItemText 
                      primary="Role"
                      secondary={<Chip label={selectedUser.role} size="small" />}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Status"
                      secondary={getStatusChip(selectedUser.status)}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Joined"
                      secondary={formatDate(selectedUser.joined)}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Verified"
                      secondary={selectedUser.isVerified ? 'Yes' : 'No'}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Email Verified"
                      secondary={selectedUser.emailVerified ? 'Yes' : 'No'}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Phone Verified"
                      secondary={selectedUser.phoneVerified ? 'Yes' : 'No'}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Document Verified"
                      secondary={selectedUser.documentVerified ? 'Yes' : 'No'}
                    />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>Activity Summary</Typography>
                <List dense>
                  <ListItem>
                    <ListItemText 
                      primary="Projects"
                      secondary={`${selectedUser.projectCount} total`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary={selectedUser.role === 'contractor' ? 'Earnings' : 'Spent'}
                      secondary={formatCurrency(selectedUser.earned || selectedUser.spent)}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Risk Score"
                      secondary={`${selectedUser.riskScore || 0}%`}
                    />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          {selectedUser && (
            <FormControl size="small" sx={{ minWidth: 160, mr: 'auto' }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={selectedUser.role || ''}
                label="Role"
                onChange={(e) => { handleSetUserRole(selectedUser.id, e.target.value); setSelectedUser((prev) => ({ ...prev, role: e.target.value })); }}
              >
                <MenuItem value="client">Client</MenuItem>
                <MenuItem value="contractor">Contractor</MenuItem>
                <MenuItem value="verifier">Verifier</MenuItem>
                <MenuItem value="project_manager">Project Manager</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
          )}
          <Button onClick={() => setUserDialogOpen(false)}>Close</Button>
          {selectedUser && (selectedUser.status === 'suspended' || selectedUser.status === 'banned') ? (
            <Button variant="contained" color="success" startIcon={<CheckCircle />} onClick={() => { handleSetUserStatus(selectedUser.id, 'active'); setUserDialogOpen(false); }}>
              Activate
            </Button>
          ) : selectedUser && (
            <Button variant="contained" color="error" startIcon={<Block />} onClick={() => { openStatusDialog([selectedUser]); setUserDialogOpen(false); }}>
              Suspend / Ban
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Alert Details Dialog */}
      <Dialog open={alertDialogOpen} onClose={() => setAlertDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogCloseButton onClick={() => setAlertDialogOpen(false)} />
        <DialogTitle>Alert Details</DialogTitle>
        <DialogContent>
          {selectedAlert && (
            <Box>
              <Typography variant="h6">{selectedAlert.title}</Typography>
              <Typography variant="body2" paragraph>
                {selectedAlert.description}
              </Typography>
              <Typography variant="body2">
                Severity: <Chip label={selectedAlert.severity} color={getSeverityColor(selectedAlert.severity)} size="small" />
              </Typography>
              <Typography variant="body2">
                Time: {formatDateTime(selectedAlert.timestamp)}
              </Typography>
              <Typography variant="body2">
                Status: {selectedAlert.resolved ? 'Resolved' : 'Active'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAlertDialogOpen(false)}>Close</Button>
          {selectedAlert && !selectedAlert.resolved && (
            <Button variant="contained" color="primary">Mark Resolved</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Content Create/Edit Dialog */}
      <Dialog open={contentDialogOpen} onClose={() => setContentDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogCloseButton onClick={() => setContentDialogOpen(false)} />
        <DialogTitle>{contentForm.id ? 'Edit Content' : 'Create Content'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  value={contentForm.type}
                  label="Type"
                  onChange={(e) => setContentForm((prev) => ({ ...prev, type: e.target.value }))}
                >
                  <MenuItem value="announcement">Announcement</MenuItem>
                  <MenuItem value="blog">Blog</MenuItem>
                  <MenuItem value="help">Help</MenuItem>
                  <MenuItem value="media">Media</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={contentForm.status}
                  label="Status"
                  onChange={(e) => setContentForm((prev) => ({ ...prev, status: e.target.value }))}
                >
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="published">Published</MenuItem>
                  <MenuItem value="archived">Archived</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Title"
                value={contentForm.title}
                onChange={(e) => setContentForm((prev) => ({ ...prev, title: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Excerpt"
                value={contentForm.excerpt}
                onChange={(e) => setContentForm((prev) => ({ ...prev, excerpt: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Body"
                multiline
                minRows={6}
                value={contentForm.body}
                onChange={(e) => setContentForm((prev) => ({ ...prev, body: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Featured Image URL"
                value={contentForm.featuredImageUrl}
                onChange={(e) => setContentForm((prev) => ({ ...prev, featuredImageUrl: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContentDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveContent}
            disabled={contentSaving || !contentForm.title}
            startIcon={contentSaving ? <CircularProgress size={16} /> : null}
          >
            {contentForm.id ? 'Save Changes' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <UserStatusDialog
        open={statusDialog.open}
        users={statusDialog.users}
        initialMode={statusDialog.mode}
        onClose={() => setStatusDialog((p) => ({ ...p, open: false }))}
        onConfirm={handleConfirmStatusChange}
      />

      {/* Permanent user deletion — type-to-confirm */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => !deleting && setDeleteDialog({ open: false, user: null, confirmText: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>Permanently delete user</DialogTitle>
        <DialogContent dividers>
          <Typography gutterBottom>
            This permanently deletes <strong>{deleteDialog.user?.email}</strong>
            {deleteDialog.user?.role ? ` (${deleteDialog.user.role})` : ''} and their profile. This cannot be undone.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Accounts with real activity (projects, bids, payments) can’t be deleted — suspend them instead.
            To confirm, type the email address below.
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder={deleteDialog.user?.email}
            value={deleteDialog.confirmText}
            onChange={(e) => setDeleteDialog((p) => ({ ...p, confirmText: e.target.value }))}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, user: null, confirmText: '' })} disabled={deleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            startIcon={<Delete />}
            disabled={deleting || deleteDialog.confirmText.trim().toLowerCase() !== (deleteDialog.user?.email || '').toLowerCase()}
            onClick={confirmDeleteUser}
          >
            {deleting ? 'Deleting…' : 'Delete permanently'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhanced Notifications */}
      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccess('')} severity="success" variant="filled">
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setError('')} severity="error" variant="filled">
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminDashboard;